import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import './home.css';

import Navigation from '../components/navigation';
import Footer from '../components/footer';

import Portfolio from './Portfolio';
import Process from './Process';
import AboutModal from '../components/aboutmodal';
import ContactModal from '../components/contactmodal';

/* ----------------------- Icons ----------------------- */
const CheckIcon = ({ size = 18 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 6L9 17l-5-5"/>
  </svg>
);

const ArrowIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 12h14m-7-7l7 7l-7 7"/>
  </svg>
);

/* ----------------------- Hero ----------------------- */
const Hero = ({ onShowPortfolio }) => (
  <section className="hero-section" id="accueil">
    <video
      className="hero-video"
      autoPlay
      loop
      muted
      playsInline
      poster="/hero-poster.jpg"
      src="https://cdn.create.vista.com/api/media/medium/742692884/stock-video-abstract-tenderness-red-silk-background-luxury-wave-cloth-satin-pastel?token="
      aria-label="Fond dynamique rose, blanc et couleur peau"
    />
    <div className="hero-overlay">
    <div className="hero-content-wrapper">
      <div className="hero-content">
        <h1 className="hero-title">
          Transformez vos idées <span className="highlight">en solutions web</span> d'exception
        </h1>

        <p className="hero-subtitle">
          Sites vitrines élégants, e-commerce performants et applications sur mesure.
        </p>

        <div className="hero-features">
          <div className="hero-feature-item">
            <CheckIcon />
            <span>Design moderne</span>
          </div>
          <div className="hero-feature-item">
            <CheckIcon />
            <span>Optimisé SEO</span>
          </div>
          <div className="hero-feature-item">
            <CheckIcon />
            <span>Responsive</span>
          </div>
        </div>
      </div>
    </div>
    </div>
  </section>
);

/* ----------------------- Services Data ----------------------- */
const servicesData = [
  {
    id: 'vitrine',
    title: 'Site vitrine professionnel',
    modalTitle: 'Création de site vitrine',
    desc: 'Présentez votre activité avec un site élégant, rapide et optimisé SEO.',
    features: ['Design personnalisé', 'Performance optimale', 'Référencement inclus'],
    prices: [
      { label: 'Essentiel', price: 'À partir de 1599€', detail: 'Page d’accueil + 3 pages internes, formulaire contact, responsive, optimisation basique SEO.' },
      { label: 'Avancé', price: 'À partir de 1999€', detail: 'Design personnalisé, animations légères, gestion de contenu, optimisation SEO avancée.' },
    ],
  },
  {
    id: 'ecommerce',
    title: 'E-commerce',
    desc: 'Lancez votre boutique en ligne avec une plateforme sécurisée et intuitive.',
    features: ['Paiement sécurisé', 'Gestion simplifiée', 'Multi-plateforme'],
    prices: [
      { label: 'Pack boutique', price: 'À partir de 2499€', detail: 'Catalogue produit, panier, paiement en ligne, pages produit, espace admin basique.' },
    ],
  },
  {
    id: 'application',
    title: 'Applications sur mesure',
    desc: "Développement d'applications web personnalisées et évolutives.",
    features: ['100% personnalisé', 'Évolutif', 'Support technique'],
    prices: [
      { label: 'Projet sur-mesure', price: 'À partir de 2999€', detail: "Étude, architecture, développement et déploiement. Prix variable selon périmètre (heure/jour)." },
    ],
  },
];

/* ----------------------- Service Modal ----------------------- */
const ServiceModal = ({ service, onClose, onStartProject }) => {
  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [onClose]);

  if (!service) return null;

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={`${service.title} — détails & tarifs`} onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <button className="modal-close" aria-label="Fermer" onClick={onClose}>✕</button>

        <div className="modal-grid">
          <div className="modal-left">
            <h3 className="modal-title">{service.modalTitle ?? service.title}</h3>
            <p className="modal-desc">{service.desc}</p>
            <h4 className="modal-subtitle">Inclus</h4>
            <ul className="modal-features">
              {service.features.map(f => (
                <li key={f} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <CheckIcon size={20} /><span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="modal-right">
            <h4 className="modal-subtitle" style={{ marginBottom: '15px' }}>Tarifs (indication)</h4>
            {service.prices.map(p => (
              <div key={p.label} className="price-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #e2e8f0', gap: '10px' }}>
                <div className="price-left">
                  <strong style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>{p.label}</strong>
                  <div style={{ fontSize: '14px', color: '#475569' }}>{p.detail}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontWeight: 400, fontSize: '16px', color: '#1e293b' }}>{p.price}</span>
                  <button className="btn btn-outline btn-sm" onClick={() => { onStartProject && onStartProject(service.title, p.label); onClose(); }}>
                    Démarrer ce projet
                  </button>
                </div>
              </div>
            ))}
            <p style={{ marginTop: '20px', fontSize: '13px', color: '#64748b' }}>
              Prix indicatifs pour un freelance junior — le tarif final dépendra du périmètre exact.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ----------------------- Service Card ----------------------- */
const ServiceCard = ({ service, onOpen }) => (
  <article className="services-card" tabIndex={0} aria-labelledby={`svc-${service.id}-title`}>
    <div className="services-card-inner">
      <h3 id={`svc-${service.id}-title`} className="services-card-title">{service.title}</h3>
      <div className="services-card-price">{service.prices?.[0] ? <span>{service.prices[0].price}</span> : null}</div>
      <p className="services-card-desc">{service.desc}</p>
      <div className="services-card-features">
        {service.features.map(f => <div key={f}><CheckIcon size={16} /><span>{f}</span></div>)}
      </div>
      <div className="services-card-actions">
        <button className="btn btn-secondary" onClick={() => onOpen(service.id)}>En savoir plus <ArrowIcon /></button>
      </div>
    </div>
  </article>
);

/* ----------------------- Services Section ----------------------- */
const Services = ({ onOpenModal }) => (
  <section className="services-section" id="services">
    <div className="services-container">
      <div className="services-header">
        <h2 className="section-title">Des services adaptés à vos besoins</h2>
        <p className="section-subtitle">Que vous soyez une entreprise, un entrepreneur ou un créatif, je développerai la solution digitale qui fera briller votre projet.</p>
      </div>
      <div className="services-content">
        {servicesData.map(s => <ServiceCard key={s.id} service={s} onOpen={onOpenModal} />)}
      </div>
    </div>
  </section>
);

/* ----------------------- CTA ----------------------- */
const CTA = ({ onStartProject }) => (
  <section className="cta-section" id="cta">
    <div className="cta-wrapper">
      <div className="cta-content">
        <div className="cta-text-column">
          <h2 className="section-title" id="contact">Prêt à lancer votre projet digital ?</h2>
          <p className="section-content">Que vous ayez besoin d'un site vitrine élégant, d'une boutique en ligne performante ou d'une application sur mesure. Je transforme vos ambitions en réalité digitale.</p>
        </div>
        <div className="cta-actions-column">
          <div className="cta-services-recap">
            <div><CheckIcon /><span>Sites vitrines modernes</span></div>
            <div><CheckIcon /><span>E-commerce performants</span></div>
            <div><CheckIcon /><span>Applications sur mesure</span></div>
            <div><CheckIcon /><span>Support technique inclus</span></div>
          </div>
          <div className="cta-buttons">
            <button className="btn btn-lg btn-outline" onClick={() => onStartProject("Site vitrine", "")}>
              Démarrer un projet
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ----------------------- Home ----------------------- */
const Home = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [contactService, setContactService] = useState("Site vitrine");
  const [contactPackage, setContactPackage] = useState("");
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [openServiceId, setOpenServiceId] = useState(null);

  const mapServiceTitleToOption = {
    'Site vitrine professionnel': 'Site vitrine',
    'E-commerce': 'E-commerce',
    'Applications sur mesure': 'Application sur mesure'
  };

  const openAbout = () => setIsAboutOpen(true);
  const closeAbout = () => setIsAboutOpen(false);

  const handleOpenModal = (id) => setOpenServiceId(id);
  const handleCloseModal = () => setOpenServiceId(null);

  const handleStartProject = (service, pack) => {
    setContactService(service);
    setContactPackage(pack || "");
    setIsContactOpen(true);
  };

  const activeService = servicesData.find(s => s.id === openServiceId) || null;

  return (
    <div className="home-root">
      <Helmet>
        <title>Gaëtan Crabeels — Développeur Web</title>
        <meta name="description" content="Développeur web spécialisé en sites vitrines, e-commerce et applications sur mesure." />
        <link rel="canonical" href="/" />
      </Helmet>

      <Navigation onOpenAbout={openAbout} onOpenContact={() => setIsContactOpen(true)} />

      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} prefilledService={contactService} prefilledPackage={contactPackage} />

      <main>
        <Hero />
        <Services onOpenModal={handleOpenModal} />
        {/* <Portfolio /> */}
        <Process />
        <CTA onStartProject={handleStartProject} />
      </main>

      <Footer onOpenAbout={openAbout} />
      <AboutModal isOpen={isAboutOpen} onClose={closeAbout} />

      {openServiceId && <ServiceModal
        service={activeService}
        onClose={handleCloseModal}
        onStartProject={(title, pack) => handleStartProject(mapServiceTitleToOption[title] || "Site vitrine", pack)}
      />}
    </div>
  );
};

export default Home;
